"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useMutation } from "@apollo/client";
import { cn, track } from "../../utils";
import TextInput from "../Form/Inputs/TextInput/TextInput";
import SubmitButton from "../SubmitButton/SubmitButton";
import ContinueWithGoogleButton from "../ContinueWithGoogleButton/ContinueWithGoogleButton";
import CheckboxInput from "../Form/Inputs/Checkbox/Checkbox";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  setSavedPath,
  setSavedFollow,
} from "../../redux/slices/savedProgressSlice";
import {
  CURRENT_USER_QUERY,
  GET_CLUSTERS_QUERY,
  GET_CLUSTER_QUERY,
  SIGN_UP_MUTATION,
  UPDATE_CLUSTER_MUTATION,
} from "../../../apollo/queries";
import formatAPIErrors from "../../utils/formatAPIErrors";
import Separator from "../Separator/Separator";
import { AnalyticsEventName } from "../../../types";

const SignUpSchema = Yup.object().shape({
  firstName: Yup.string().required("Please enter your first name."),
  lastName: Yup.string().required("Please enter your last name."),
  email: Yup.string()
    .email("Please enter a valid email.")
    .required("Please enter your email."),
  password: Yup.string()
    .min(8, "Password must be at least eight characters long.")
    .max(16, "Password can't be longer than 16 characters.")
    .matches(
      /(?=.*[a-z])/,
      "Password should include at least one lowercase letter.",
    )
    .matches(
      /(?=.*[A-Z])/,
      "Password should include at least one uppercase letter.",
    )
    .required("Please enter a password."),
  passwordConfirmation: Yup.string()
    .required("Please confirm your password.")
    .oneOf([Yup.ref("password")], "Passwords must match"),
  privacy: Yup.bool()
    .oneOf([true], "Please accept our privacy policy")
    .required(),
});

const INPUTS = [
  {
    name: "firstName",
    type: "text",
    placeholder: "First name*",
  },
  {
    name: "lastName",
    type: "text",
    placeholder: "Last name*",
  },
  {
    name: "email",
    type: "email",
    placeholder: "Email address*",
  },
  {
    name: "password",
    type: "password",
    placeholder: "Password*",
  },
  {
    name: "passwordConfirmation",
    type: "password",
    placeholder: "Confirm password*",
  },
];

const SignUp = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [signUpMutation] = useMutation(SIGN_UP_MUTATION);

  // shared cluster user would like to contribute to
  const clusterId = searchParams.get("clusterId");

  // utm params from unbounce
  const utmSource = searchParams.get("utm_source");
  const utmMedium = searchParams.get("utm_medium");
  const utmCampaign = searchParams.get("utm_campaign");
  const utmContent = searchParams.get("utm_content");
  const utmTerm = searchParams.get("utm_term");

  const dispatch = useAppDispatch();
  const { savedPath, savedFollow } = useAppSelector(
    (state) => state.savedProgress,
  );

  const [updateCluster] = useMutation(UPDATE_CLUSTER_MUTATION, {
    refetchQueries: [
      GET_CLUSTERS_QUERY,
      {
        query: GET_CLUSTER_QUERY,
        variables: {
          input: {
            id: savedFollow,
          },
        },
      },
    ],
  });

  return (
    <div className="flex flex-col w-full md:max-w-[640px]">
      <Formik
        initialValues={{
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          passwordConfirmation: "",
          privacy: false,
        }}
        validationSchema={SignUpSchema}
        onSubmit={async (
          { firstName, lastName, email, password },
          { setSubmitting, resetForm, setErrors },
        ) => {
          await signUpMutation({
            variables: {
              input: {
                firstName,
                lastName,
                email: email.toLowerCase(),
                password,
                utmSource,
                utmMedium,
                utmCampaign,
                utmContent,
                utmTerm,
              },
            },
            onCompleted: async ({ signUp: { user } }) => {
              if (user) {
                // if logged in redirect to user feed
                resetForm();

                // analytics
                track(AnalyticsEventName.SIGNUP, {
                  User: user.id,
                });

                if (savedFollow) {
                  // TODO: might need to do same mutations as in FollowButton
                  await updateCluster({
                    variables: {
                      input: {
                        id: savedFollow,
                        data: {
                          follow: true,
                        },
                      },
                    },
                  });

                  dispatch(setSavedFollow(null));
                }

                if (savedPath) {
                  router.push(savedPath);
                  dispatch(setSavedPath(null));
                } else if (clusterId) {
                  // user is signing up wanting to contribute to a shared cluster
                  router.push(
                    `/spark/add?clusterId=${clusterId}&sharedCluster=true`,
                  );
                } else {
                  router.push("/sparks");
                }
              }

              // TODO: might need to use apollo equivalent to finally block - will we get here if error?
              setSubmitting(false);
            },
            onError: (error) => {
              const formattedErrors = formatAPIErrors(error);
              setErrors(formattedErrors);
            },
            update: (
              cache,
              {
                data: {
                  signUp: { user },
                },
              },
            ) => {
              // update apollo cache
              cache.writeQuery({
                query: CURRENT_USER_QUERY,
                data: {
                  currentUser: user,
                },
              });
            },
          });
        }}
      >
        {({ handleSubmit, isSubmitting, errors }) => (
          <Form
            className="flex flex-col text-lg w-full p-0"
            onSubmit={handleSubmit}
          >
            <div className="flex flex-col gap-y-4 mb-8">
              {INPUTS.map(({ name, type, placeholder }) => (
                <TextInput
                  key={name}
                  name={name}
                  type={type}
                  placeholder={placeholder}
                />
              ))}
            </div>
            <span className="self-start text-base text-grey flex items-baseline mb-8">
              <CheckboxInput
                className={cn({
                  "text-red": errors.privacy,
                })}
                id="privacy"
                label="children"
                name="privacy"
                type="checkbox"
              >
                {errors.privacy ? "Please accept our" : "Accept our"}&nbsp;
                <a
                  className="text-blue"
                  href="https://sparksapp.io/terms-of-use"
                  target="_blank"
                  rel="noreferrer"
                >
                  Terms of use
                </a>
              </CheckboxInput>
            </span>
            <SubmitButton
              className="bg-blue"
              text="Sign Up"
              submittingText="Signing Up"
              isSubmitting={isSubmitting}
            />
          </Form>
        )}
      </Formik>
      <Separator>OR</Separator>
      <ContinueWithGoogleButton />
      <span className="text-center text-sm mt-2">
        Already have an account? &nbsp;
        <Link href="/sign-in">Sign In</Link>
      </span>
    </div>
  );
};

export default SignUp;
